import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EllipsisVertical } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useFormContext, useWatch } from 'react-hook-form'
import { useLocalStorage } from 'react-use'
import { formatUnits, parseUnits } from 'viem'

import {
  Button,
  List,
  Message,
  TokenBalance,
  TokenSelector as FormTokenSelector,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { Dialog } from '~/components/component-library/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown'
import {
  useAutoSwapSelection,
  useSelectedSwapToken,
} from '~/components/forms/swap/hooks/useSwapHelpers'
import { PaymentSourceField } from '~/components/forms/transfer/fields/PaymentSource'
import { useIsInsufficientFunds } from '~/components/forms/transfer/hooks/useTransferForm'
import { useTransferQuote } from '~/components/forms/transfer/hooks/useTransferQuote'
import { AddMultiRecipientDialog } from '~/components/forms/transfer/multi/AddMultiRecipientDialog'
import { AddRecipientDialog } from '~/components/forms/transfer/multi/AddRecipientDialog'
import {
  getValidatedRecipients,
  PersistedSelectedToken,
  TransferFormValues,
  ValidatedRecipient,
} from '~/components/forms/transfer/transferSchema'
import { SupportedChainId } from '~/constants/chains'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { TokenMetadata, useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'

export const MultiRecipientSection = ({
  sender,
  tokenFilter,
  chainFilter,
  hasSendRuleRestrictions,
  shouldAutoFocus,
  isLoading,
}: {
  sender: IAddress
  tokenFilter: (token: {
    chainId: SupportedChainId
    address: string
  }) => boolean
  chainFilter: (chainId: SupportedChainId) => boolean
  hasSendRuleRestrictions?: boolean
  shouldAutoFocus?: boolean
  isLoading?: boolean
}) => {
  const { smartAccounts } = useSmartAccounts()
  const { org } = useActiveOrg()

  const senderAccount = smartAccounts.find(
    (account) => account.address === sender,
  )

  const [localStorageIsUSDInput, setLocalStorageIsUSDInput] =
    useLocalStorage<boolean>(
      `splits.smartAccounts.amountSelector.isUSDInput.multiRecipientAmount`,
      true,
    )

  const { control, setValue, getValues, resetField } =
    useFormContext<TransferFormValues>()

  const transferToken = useWatch({ control, name: 'transferToken' })
  const recipients = useWatch({ control, name: 'recipients' })
  const isUSDInput = useWatch({
    control,
    name: 'isUSDInput',
    defaultValue: localStorageIsUSDInput,
  })

  const transferTokenMetadata = useTokenMetadata({
    address: transferToken?.token,
    chainId: transferToken?.chainId,
    includePrice: true,
  })

  const [defaultSelectedToken, setDefaultSelectedToken] = useLocalStorage<
    PersistedSelectedToken | undefined
  >(`splits.defaultSelectedToken.${org?.id}`, undefined)

  const isInsufficientFunds = useIsInsufficientFunds({
    sender,
    token: transferToken?.token,
    chainId: transferToken?.chainId,
    amount: transferToken?.amount,
  })

  // update recipient amounts to maintain the USD value when the token changes
  useUpdateRecipientAmountsInUSD()

  /**
   * We need to track the isUSDInput value in the form context (so it can be toggled within the dialog)
   * that means that we need to initialize the form field with the localStorage value on mount
   * and we need to track the previous value of isUSDInput
   */
  useEffect(() => {
    const currentFormValue = getValues('isUSDInput')
    if (
      currentFormValue === undefined &&
      localStorageIsUSDInput !== undefined
    ) {
      setValue('isUSDInput', localStorageIsUSDInput)
    }
  }, [localStorageIsUSDInput, setValue, getValues])

  // set isUSDInput from form to local storage
  useEffect(() => {
    setLocalStorageIsUSDInput(isUSDInput)
  }, [isUSDInput, setLocalStorageIsUSDInput])

  // set token and chainId from local storage to form (mount-only)
  useEffect(() => {
    if (defaultSelectedToken && !transferToken?.token && !isLoading) {
      const isValid =
        (!chainFilter || chainFilter(defaultSelectedToken.chainId)) &&
        (!tokenFilter ||
          tokenFilter({
            chainId: defaultSelectedToken.chainId,
            address: defaultSelectedToken.token,
          }))
      if (isValid) {
        setValue('transferToken.token', defaultSelectedToken.token)
        setValue('transferToken.chainId', defaultSelectedToken.chainId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // calculate the total amount of the transfer
  useEffect(() => {
    const totalAmount = recipients.reduce((acc, recipient) => {
      return (
        acc +
        parseUnits(
          recipient.amount ?? '0',
          transferTokenMetadata?.decimals ?? 18,
        )
      )
    }, BigInt(0))
    setValue(
      'transferToken.amount',
      formatUnits(totalAmount, transferTokenMetadata?.decimals ?? 18),
    )
  }, [recipients, setValue, transferTokenMetadata?.decimals])

  const resetForm = useCallback(() => {
    setValue('calls', [])
    setValue('swapData', undefined)
  }, [setValue])

  const { onSelectAutoSwap } = useAutoSwapSelection()
  const selectedToken = useSelectedSwapToken()

  const selectedTokenMetadata: TokenMetadata | undefined = useTokenMetadata({
    address: selectedToken?.address,
    chainId: selectedToken?.chainId,
  })

  const hasTokenPrice = !!transferTokenMetadata?.price

  const pathname = usePathname()
  const isScheduleVariant = pathname?.startsWith('/schedules')

  const { quoteError } = useTransferQuote()

  const hasValidTransferToken: boolean =
    !!senderAccount && !!transferToken?.token && !!transferToken?.chainId

  // At least one recipient has an address, and every such recipient is on a VM
  // compatible with the destination token (mirrors single-send's per-receiver
  // compatibility gate). Memoized so it doesn't re-validate every recipient on
  // each quote-poll re-render; `destinationTokenAddress` is a const so it
  // narrows inside the closure.
  const destinationTokenAddress = transferToken?.token
  const recipientsCompatibleWithToken = useMemo(() => {
    const validRecipients = getValidatedRecipients(recipients)
    return (
      !!destinationTokenAddress &&
      Address.validate(destinationTokenAddress) &&
      validRecipients.length > 0 &&
      validRecipients.every(
        (recipient) =>
          Address.validate(recipient.receiver.address) &&
          Address.compatible(
            destinationTokenAddress,
            recipient.receiver.address,
          ),
      )
    )
  }, [recipients, destinationTokenAddress])

  // The always-visible Payment source input has everything it needs (sender, a
  // priced + valid token, an amount, and ≥1 compatible recipient).
  // `recipientsCompatibleWithToken` already validates the token, so it isn't
  // re-checked here.
  const isPaymentSourceReady =
    !!sender &&
    hasTokenPrice &&
    parseFloat(transferToken?.amount ?? '0') > 0 &&
    !!transferToken?.chainId &&
    recipientsCompatibleWithToken

  const showInsufficientFundsWarning =
    isInsufficientFunds &&
    !hasTokenPrice &&
    hasValidTransferToken &&
    !isScheduleVariant

  return (
    <>
      <FormTokenSelector<TransferFormValues>
        control={control}
        hideAmountInput
        label="Recipient gets"
        addressInputName="transferToken.token"
        chainIdInputName="transferToken.chainId"
        filterAccountAddress={sender}
        showBalances
        sortBy="frequency"
        tokenFilter={(token) =>
          tokenFilter({
            chainId: token.chainId as SupportedChainId,
            address: token.address,
          })
        }
        chainFilter={(chainId) => chainFilter(chainId as SupportedChainId)}
        // Contact send rules are a strict allowlist. Relay and external
        // tokens must also pass the filter.
        strictTokenFilter={hasSendRuleRestrictions}
        onTokenSelect={(token) => {
          setDefaultSelectedToken({
            token: token.address as IAddress,
            chainId: token.chainId as SupportedChainId,
          })
          resetForm()
        }}
        onTokenClear={() => {
          resetField('transferToken.token')
          resetField('transferToken.chainId')
          resetForm()
        }}
      />
      <MultiRecipientList shouldAutoFocus={shouldAutoFocus} />
      {showInsufficientFundsWarning && (
        <InsufficientFundsWarning
          tokenSymbol={transferTokenMetadata?.symbol ?? ''}
        />
      )}
      <PaymentSourceField
        ready={isPaymentSourceReady}
        sender={sender}
        destinationToken={transferToken}
        destinationTokenSymbol={transferTokenMetadata?.symbol}
        selectedToken={selectedToken}
        selectedTokenSymbol={selectedTokenMetadata?.symbol}
        onSelect={onSelectAutoSwap}
        quoteError={quoteError}
        disabledReason="Add a recipient, token, and amount first"
        // Multisend doesn't support bridging yet, so offer same-chain sources only.
        preventBridge
      />
    </>
  )
}

const InsufficientFundsWarning = ({ tokenSymbol }: { tokenSymbol: string }) => {
  return (
    <Message variant="danger" title={`Not enough ${tokenSymbol}`}>
      <span>
        You do not have enough {tokenSymbol} to submit the transaction
      </span>
    </Message>
  )
}

/**
 * When the token changes and usd is being entered, the amount should be the same in the new token
 * This hook updates the recipient amounts to maintain the USD value
 */
const useUpdateRecipientAmountsInUSD = () => {
  const { control, setValue } = useFormContext<TransferFormValues>()

  const transferToken = useWatch({ control, name: 'transferToken' })
  const recipients = useWatch({ control, name: 'recipients' })
  const isUSDInput = useWatch({ control, name: 'isUSDInput' })

  const [lastDefinedTransferToken, setLastDefinedTransferToken] = useState<{
    token: IAddress
    chainId: SupportedChainId
  } | null>(null)

  // Initialize with false, not the localStorage-influenced value
  const prevIsUSDInputRef = useRef<boolean>(false)

  // when the token changes and usd is being entered, the amount should be the same in the new token
  const { convertTokenToUSD, convertUSDToToken } = useTokenPrices()

  // Get metadata for the previous token (persists across temporary selector clears)
  const lastDefinedTokenMetadata = useTokenMetadata({
    address: lastDefinedTransferToken?.token,
    chainId: lastDefinedTransferToken?.chainId,
  })

  const updateRecipientUsdValuesToNewToken = useCallback(
    (previousToken: { token: IAddress; chainId: SupportedChainId }) => {
      // Only convert if we're actually in USD input mode
      if (!isUSDInput) return
      if (!transferToken?.token || !transferToken?.chainId) return

      const updatedRecipients = recipients.map((recipient) => {
        if (!recipient.amount || recipient.amount === '0') return recipient

        const usdValue = convertTokenToUSD({
          token: previousToken.token,
          amount: parseUnits(
            recipient.amount,
            lastDefinedTokenMetadata?.decimals ?? 18,
          ),
          chainId: previousToken.chainId,
        })

        // If USD value not available, keep original amount
        if (usdValue === undefined) return recipient

        const newTokenAmount = convertUSDToToken(
          usdValue,
          transferToken.token as string,
          transferToken.chainId,
        )

        return {
          ...recipient,
          amount: newTokenAmount,
        }
      })

      setValue('recipients', updatedRecipients)
    },
    [
      isUSDInput,
      transferToken,
      recipients,
      setValue,
      convertTokenToUSD,
      convertUSDToToken,
      lastDefinedTokenMetadata?.decimals,
    ],
  )

  // when the token changes and we're entering USD values, update the recipient amounts to maintain the USD value
  // even if the token selector briefly clears during a chain switch
  useEffect(() => {
    const hasToken = !!transferToken?.token && !!transferToken?.chainId
    const tokenChanged =
      hasToken &&
      lastDefinedTransferToken &&
      (transferToken.token !== lastDefinedTransferToken.token ||
        transferToken.chainId !== lastDefinedTransferToken.chainId)
    const wasUSDInput = prevIsUSDInputRef.current

    if (tokenChanged && wasUSDInput && isUSDInput && lastDefinedTransferToken) {
      updateRecipientUsdValuesToNewToken(lastDefinedTransferToken)
    }

    if (
      hasToken &&
      (!lastDefinedTransferToken ||
        lastDefinedTransferToken.token !== transferToken.token ||
        lastDefinedTransferToken.chainId !== transferToken.chainId)
    ) {
      setLastDefinedTransferToken({
        token: transferToken.token as IAddress,
        chainId: transferToken.chainId as SupportedChainId,
      })
    }

    prevIsUSDInputRef.current = !!isUSDInput
  }, [
    transferToken,
    isUSDInput,
    lastDefinedTransferToken,
    updateRecipientUsdValuesToNewToken,
  ])
}

const useRemoveRecipient = () => {
  const { control, setValue } = useFormContext<TransferFormValues>()
  const recipients = useWatch({ control, name: 'recipients' })

  const removeRecipient = useCallback(
    (index: number) => {
      const newRecipients = [...recipients]
      newRecipients.splice(index, 1)
      setValue('recipients', newRecipients)
    },
    [recipients, setValue],
  )

  return { removeRecipient }
}

const MultiRecipientList = ({
  shouldAutoFocus,
}: {
  shouldAutoFocus?: boolean
}) => {
  const { removeRecipient } = useRemoveRecipient()
  const { setGlobalModalState } = useAuth()
  const [recipientDialogState, setRecipientDialogState] = useState<{
    isOpen: boolean
    type: 'add' | 'edit'
    existingRecipientIndex?: number
  } | null>(null)

  const { control, setValue } = useFormContext<TransferFormValues>()

  const transferToken = useWatch({ control, name: 'transferToken' })
  const recipients = useWatch({ control, name: 'recipients' })

  const validRecipients = recipients
    .map((recipient, index) => ({ recipient, originalIndex: index }))
    .filter(
      (
        entry,
      ): entry is typeof entry & {
        recipient: ValidatedRecipient
      } => !!entry.recipient.receiver.address,
    )

  const transferTokenMetadata = useTokenMetadata({
    address: transferToken?.token,
    chainId: transferToken?.chainId,
  })

  // should not be able to select a recipient without a token selected first
  const disableAddRecipientsButton = !transferToken?.token

  return (
    <>
      <List
        label="Recipients"
        secondaryDisplay={
          <Button
            variant="secondaryAction"
            type="button"
            isDisabled={disableAddRecipientsButton}
            eventName="clickedAddMultipleRecipients"
            onClick={() => {
              setGlobalModalState({
                isOpen: true,
                content: (
                  <AddMultiRecipientDialog
                    transferToken={transferToken}
                    defaultRecipients={recipients}
                    onAdd={(recipients) => {
                      setValue('recipients', recipients)
                    }}
                    onClose={() => {
                      setGlobalModalState({
                        isOpen: false,
                        content: undefined,
                      })
                    }}
                    shouldAutoFocus={shouldAutoFocus}
                  />
                ),
              })
            }}
          >
            Add multiple
          </Button>
        }
        items={validRecipients.map(({ recipient, originalIndex }) => ({
          key: `recipient-${originalIndex}-${recipient.receiver.address}`,
          content: (
            <AddressDisplay
              address={recipient.receiver.address}
              chainId={transferToken?.chainId}
              layout="two-line"
            />
          ),
          meta:
            transferToken?.token && transferToken?.chainId ? (
              <TokenBalance
                address={transferToken?.token}
                chainId={transferToken?.chainId}
                tokenAmount={parseUnits(
                  recipient.amount,
                  transferTokenMetadata?.decimals ?? 18,
                )}
                layout="standard"
                size="base"
                direction="neutral"
              />
            ) : undefined,
          actionIcon: (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex h-5 w-5 cursor-pointer items-center justify-center">
                <EllipsisVertical className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom" align="end" className="w-48">
                <DropdownMenuItem
                  onSelect={() => {
                    setRecipientDialogState({
                      isOpen: true,
                      type: 'edit',
                      existingRecipientIndex: originalIndex,
                    })
                  }}
                >
                  Edit...
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => removeRecipient(originalIndex)}
                >
                  Remove
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
        }))}
        cta={{
          label: 'Add recipient',
          isDisabled: disableAddRecipientsButton,
          tooltip: disableAddRecipientsButton
            ? 'Please select a token first to add a recipient'
            : undefined,
          onClick: () => {
            if (disableAddRecipientsButton) return
            setRecipientDialogState({
              isOpen: true,
              type: 'add',
            })
          },
        }}
      />
      <Dialog
        open={recipientDialogState?.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRecipientDialogState(null)
          }
        }}
      >
        {recipientDialogState?.isOpen && (
          <AddRecipientDialog
            existingRecipient={
              recipients[recipientDialogState?.existingRecipientIndex ?? -1]
            }
            existingIndex={recipientDialogState?.existingRecipientIndex}
            shouldAutoFocus={shouldAutoFocus ?? false}
            transferToken={transferToken}
            addRecipient={(recipient) => {
              setValue('recipients', [...recipients, recipient])
            }}
            editRecipient={(recipient) => {
              const newRecipients = [...recipients]
              newRecipients[
                recipientDialogState?.existingRecipientIndex ?? -1
              ] = recipient
              setValue('recipients', newRecipients)
            }}
            onClose={() => {
              setRecipientDialogState(null)
            }}
          />
        )}
      </Dialog>
    </>
  )
}
